import { useState, useEffect } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { useGetUsers } from '../api/useGetUsers';

interface Option {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
}

export const UsersDropdown = ({ value, onChange }: Props) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  useEffect(() => {
    if (!search) {
      setDebouncedSearch('');
      return;
    }
    const timer = setTimeout(() => { setDebouncedSearch(search); }, 1000);
    return () => { clearTimeout(timer); };
  }, [search]);

  const { data, isLoading } = useGetUsers({ q: debouncedSearch || undefined });

  const { users = [] } = data?.payload ?? {};

  const options = users.map((user) => ({
    value: user.uid,
    label: `${user.first_name} ${user.last_name} (${user.email})`,
  }));

  const mergedOptions =
    selectedOption && !options.find((o) => o.value === selectedOption.value)
      ? [selectedOption, ...options]
      : options;

  const handleChange = (v: string) => {
    if (!v) { setSelectedOption(null); onChange(''); return; }
    const found = options.find((o) => o.value === v);
    if (found) { setSelectedOption(found); }
    onChange(v);
  };

  return (
    <SearchableSelect
      options={mergedOptions}
      value={value}
      onChange={handleChange}
      onSearchChange={setSearch}
      placeholder="Select a user"
      isLoading={isLoading}
    />
  );
};
